import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { fetchChargeCycleMetrics } from '../../../services/chCycleMetricsService';

const timeFilters = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

const metricOptions = [
  { value: 'cycle', label: 'Battery Cycles' },
  { value: 'ah_partial', label: 'AH Partial' }
];

const ChargeCycleMetrics = ({ emsName }) => {
  const [timeFilter, setTimeFilter] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('cycle');

  const fetchData = useCallback(async () => {
    if (!emsName) return;
    
    try {
      setLoading(true);
      setError(null);
      // Format date using local timezone
      const formattedDate = selectedDate.toLocaleDateString('en-CA'); // Returns YYYY-MM-DD format
      const response = await fetchChargeCycleMetrics(
        emsName,
        timeFilter,
        formattedDate
      );
      console.log('Charge cycle metrics response:', response);
      // Enhanced data validation and processing
      const processedData = response.data
        .map((item, index) => {
          const timestamp = Number(item.timestamp);
          const cycle = Number(item.cycle || 0);
          const ah_partial = Number(item.ah_partial || 0);
          const hour_timestamp = item.hour_timestamp ? Number(item.hour_timestamp) : null;

          // Skip invalid data points
          if (isNaN(timestamp) || (timeFilter === 'weekly' && isNaN(hour_timestamp))) {
            console.warn('Invalid data point:', item);
            return null;
          }

          return {
            uniqueId: `${timeFilter}-${timestamp}-${index}`,
            timestamp,
            cycle: isNaN(cycle) ? 0 : cycle,
            ah_partial: isNaN(ah_partial) ? 0 : ah_partial,
            hour_timestamp: hour_timestamp
          };
        })
        .filter(Boolean) // Remove null entries
        .sort((a, b) => {
          const aTime = timeFilter === 'weekly' ? (a.hour_timestamp || a.timestamp) : a.timestamp;
          const bTime = timeFilter === 'weekly' ? (b.hour_timestamp || b.timestamp) : b.timestamp;
          return aTime - bTime;
        });

      console.log("Processed data:", {
        count: processedData.length,
        firstItem: processedData[0],
        lastItem: processedData[processedData.length - 1],
        timeFilter
      });
      
      setData(processedData);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to load charge cycle metrics');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [emsName, timeFilter, selectedDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatXAxis = useCallback((timestamp) => {
    const date = new Date(timestamp);
    switch(timeFilter) {
      case 'monthly':
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      case 'weekly':
        return date.toLocaleDateString('en-US', { weekday: 'short' });
      case 'daily':
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      default:
        return date.toLocaleString();
    }
  }, [timeFilter]);

  const renderChart = useCallback(() => {
    if (!data.length) return null;

    const chartCommonProps = {
      margin: { top: 5, right: 30, left: 20, bottom: 5 },
      height: 400,
      width: 900,
    };

    // Generate unique ticks based on data
    const generateTicks = () => {
      const tickCount = Math.min(15, data.length);
      const step = Math.ceil(data.length / tickCount);
      const timeKey = timeFilter === 'weekly' ? 'hour_timestamp' : 'timestamp';
      
      return data
        .filter((_, index) => index % step === 0)
        .map(item => item[timeKey])
        .filter(Boolean);
    };

    const commonXAxisProps = {
      dataKey: timeFilter === 'weekly' ? 'hour_timestamp' : 'timestamp',
      tickFormatter: formatXAxis,
      type: "number",
      domain: ['auto', 'auto'],
      scale: "time",
      tick: { fontSize: 12 },
      ticks: generateTicks(),
      allowDuplicatedCategory: false
    };

    // Validate domain values for Y axis
    const getYAxisDomain = (dataKey) => {
      const values = data.map(item => Number(item[dataKey])).filter(val => !isNaN(val));
      if (!values.length) return [0, 100];
      const min = Math.min(...values);
      const max = Math.max(...values);
      return [min - (min * 0.1), max + (max * 0.1)];
    };

    switch (selectedMetric) {
      case 'cycle':
        return (
          <Box sx={{ 
            mb: 3, 
            width: chartCommonProps.width,
            height: chartCommonProps.height
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart 
                data={data}
                {...chartCommonProps}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis {...commonXAxisProps} />
                <YAxis 
                  domain={getYAxisDomain('cycle')}
                  tickMargin={5} // Add margin between ticks and axis
                />
                <Tooltip 
                  labelFormatter={(label) => new Date(label).toLocaleString()}
                  formatter={(value) => [Math.round(value), 'Cycles']}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="cycle" 
                  name="Battery Cycles"
                  stroke="#8884d8" 
                  dot={false}
                  isAnimationActive={false}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        );

      case 'ah_partial':
        return (
          <Box sx={{ 
            width: chartCommonProps.width,
            height: chartCommonProps.height
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={data}
                margin={chartCommonProps.margin}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey={timeFilter === 'weekly' ? 'hour_timestamp' : 'timestamp'}
                  tickFormatter={formatXAxis}
                  type="category"
                  scale="band"
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  domain={getYAxisDomain('ah_partial')}
                  tickMargin={5} // Add margin between ticks and axis
                />
                <Tooltip
                  cursor={false}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div style={{ 
                          backgroundColor: '#fff',
                          padding: '10px',
                          border: '1px solid #ccc',
                          borderRadius: '4px'
                        }}>
                          <p style={{ margin: 0 }}>{`Time: ${new Date(label).toLocaleString()}`}</p>
                          <p style={{ margin: 0, color: '#82ca9d' }}>
                            {`AH Partial: ${Math.round(payload[0].value)}`}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="ah_partial"
                  name="AH Partial"
                  fill="#82ca9d"
                  isAnimationActive={false}
                  onMouseOver={(data) => {
                    console.log("Bar hover data:", data);
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        );

      default:
        return null;
    }
  }, [data, selectedMetric, formatXAxis, timeFilter]);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ width: 'fit-content' }}>
      <Box sx={{ 
        display: 'flex',
        gap: 2,
        mb: 2,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 1,
        justifyContent: 'flex-start',
        '& .MuiFormControl-root': {
          minWidth: 180
        }
      }}>
        <FormControl size="small">
          <InputLabel>Metric</InputLabel>
          <Select
            value={selectedMetric}
            label="Metric"
            onChange={(e) => setSelectedMetric(e.target.value)}
            MenuProps={{
              sx: {
                '& .MuiMenu-paper': {
                  '& .MuiMenuItem-root': {
                    padding: '8px 16px',
                    margin: '4px 0',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }
                }
              }
            }}
          >
            {metricOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small">
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeFilter}
            label="Time Range"
            onChange={(e) => setTimeFilter(e.target.value)}
            MenuProps={{
              sx: {
                '& .MuiMenu-paper': {
                  '& .MuiMenuItem-root': {
                    padding: '8px 16px',
                    margin: '4px 0',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }
                }
              }
            }}
          >
            {timeFilters.map((filter) => (
              <MenuItem key={filter.value} value={filter.value}>
                {filter.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Select Date"
            value={selectedDate}
            onChange={(date) => date && setSelectedDate(date)}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true
              }
            }}
          />
        </LocalizationProvider>
      </Box>

      {data.length > 0 ? (
        <Box sx={{ width: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            {selectedMetric === 'cycle' ? 'Battery Cycles' : 'AH Partial'}
          </Typography>
          {renderChart()}
        </Box>
      ) : (
        <Alert severity="info">No data available for the selected period</Alert>
      )}
    </Box>
  );
};

export default React.memo(ChargeCycleMetrics);